import { useCallback, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';

const INSTALLABLE_EVENT = 'pwa-installable';

let deferredPrompt = null;

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredPrompt = event;
    window.dispatchEvent(new Event(INSTALLABLE_EVENT));
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
  });
}

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.matchMedia('(display-mode: fullscreen)').matches ||
  window.navigator.standalone === true;

function PWAInstallPrompt() {
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [installable, setInstallable] = useState(() => Boolean(deferredPrompt));

  useEffect(() => {
    if (isStandalone()) setDismissed(true);
  }, []);

  useEffect(() => {
    const onInstallable = () => setInstallable(Boolean(deferredPrompt));
    const onInstalled = () => {
      setInstallable(false);
      setDismissed(true);
    };
    window.addEventListener(INSTALLABLE_EVENT, onInstallable);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener(INSTALLABLE_EVENT, onInstallable);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      deferredPrompt = null;
      setInstallable(false);
      if (choice?.outcome === 'accepted') setDismissed(true);
    } finally {
      setInstalling(false);
    }
  }, []);

  const dismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  const visible =
    installable && !isStandalone() && !dismissed && pathname !== '/scan';

  useEffect(() => {
    document.body.classList.toggle('has-install-prompt', visible);
    return () => document.body.classList.remove('has-install-prompt');
  }, [visible]);

  if (!visible) return null;

  return (
    <Modal
      show
      onHide={dismiss}
      backdrop={false}
      keyboard={false}
      enforceFocus={false}
      autoFocus={false}
      restoreFocus={false}
      className="pwaInstallModal"
      aria-labelledby="install-title"
    >
      <Modal.Body>
        <div>
          <h2 id="install-title" className="pwaInstallBanner__title">
            Install Social Collage
          </h2>
          <p className="pwaInstallBanner__hint">
            Add this kiosk app to the home screen for fullscreen access.
          </p>
        </div>
        <div className="pwaInstallBanner__actions">
          <button
            type="button"
            className="pwaInstallBanner__button pwaInstallBanner__button--primary"
            onClick={install}
            disabled={installing}
          >
            {installing ? 'Installing…' : 'Install app'}
          </button>
          <button
            type="button"
            className="pwaInstallBanner__button"
            onClick={dismiss}
            disabled={installing}
          >
            Not now
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default PWAInstallPrompt;
